/**
 * scrollLock
 *
 * Im OSMD-Vorschau-Container soll **ausschließlich der Nutzer** scrollen. Einen Browser-Schalter
 * dafür gibt es nicht – wohl aber die Beobachtung, dass Nutzer-Scrollen (Mausrad, Touch,
 * Scrollbar-Zug, Tastatur) **an den DOM-APIs vorbei** im Browser passiert. Wer also die
 * programmatischen Wege sperrt, verliert kein Nutzer-Scrolling:
 *
 * - `el.scrollTo()` / `el.scrollBy()` / `el.scroll()` / `el.scrollTop = …` / `el.scrollLeft = …`
 *   → {@link lockScrollContainer} überschattet sie mit eigenen Properties auf der Instanz.
 * - `child.scrollIntoView()` scrollt **jede** Vorfahren-Scrollbox (Container **und** Fenster) und
 *   geht an den Container-APIs vorbei → muss am aufrufenden Element neutralisiert werden
 *   ({@link neutralizeScrollIntoView}; Verursacher ist OSMDs Cursor-`<img>`).
 * - `el.focus()` → `shared/focus.ts` (`focusWithoutScroll`).
 *
 * Warum eine Laufzeit-Sperre und nicht nur Konfiguration: `followCursor: false` & Co. sind reine
 * Zusagen im Quelltext – und die sind schon zurückgefallen (Commit `528064c` schrieb
 * `followCursor: true` direkt unter den „nicht rückbauen"-Kommentar). Details in
 * `webapp/CLAUDE.md`, Gotcha „Kein Auto-Scroll zur Partitur".
 *
 * Bewusst **nicht** abgedeckt: browser-eigene Anpassungen (Scroll-Anchoring, Seite-Suchen,
 * Fragment-Navigation, Scroll-Restore beim Reload). Die sind nutzergetrieben bzw. erhaltend.
 */

/** Markiert ein Element, dessen `scrollIntoView` bereits neutralisiert ist (Idempotenz). */
const NEUTRALIZED = Symbol('scrollLock.neutralized');

/** Während {@link withProgrammaticScroll} laufen die nativen Implementierungen wieder durch. */
let bypass = false;

function warn(what: string): void {
    if (!import.meta.env.DEV) return;
    console.warn(
        `[scrollLock] ${what} im OSMD-Container verworfen – dort scrollt nur der Nutzer. ` +
        'Absichtlich? Dann in withProgrammaticScroll(...) kapseln.',
        new Error('scrollLock stack').stack,
    );
}

/**
 * Hebt die Sperre für die Dauer von `fn` auf – der explizite Ausweg für bewusstes App-Scrolling.
 *
 * **Nur synchron:** Ein `await` im Callback würde das Flag über die Grenze hinaus offen lassen.
 * Wer asynchron scrollen muss, kapselt nur den scrollenden Aufruf selbst.
 */
export function withProgrammaticScroll<T>(fn: () => T): T {
    const previous = bypass;
    bypass = true;
    try {
        return fn();
    } finally {
        bypass = previous;
    }
}

/**
 * Neutralisiert `scrollIntoView` auf **einem** Element. Idempotent – ein zweiter Aufruf ist billig.
 *
 * Gedacht für OSMDs Cursor-`<img>`: `Cursor.reset()` läuft über `updateWithTimestamp()`, das
 * `cursorElement.scrollIntoView({block:'center'})` ruft. Weil OSMD die Cursor bei **jedem**
 * `render()` neu erzeugt (`enableOrDisableCursors` → `new Cursor(...)` → frisches `<img>`), muss
 * das nach jedem Render erneut angewandt werden.
 */
export function neutralizeScrollIntoView(el: Element): void {
    const carrier = el as Element & {[NEUTRALIZED]?: true};
    if (carrier[NEUTRALIZED]) return;
    const native = el.scrollIntoView.bind(el);
    Object.defineProperty(el, 'scrollIntoView', {
        configurable: true,
        writable: true,
        value: (...args: Parameters<Element['scrollIntoView']>): void => {
            if (bypass) {
                native(...args);
                return;
            }
            warn('scrollIntoView()');
        },
    });
    carrier[NEUTRALIZED] = true;
}

/** Property-Namen, die auf dem gesperrten Container überschattet werden. */
const SCROLL_METHODS = ['scrollTo', 'scrollBy', 'scroll'] as const;
const SCROLL_OFFSETS = ['scrollTop', 'scrollLeft'] as const;

/**
 * Sperrt programmatisches Scrollen **auf diesem Container**. Rückgabe hebt die Sperre wieder auf
 * (Prototype-Verhalten ist danach zurück).
 *
 * Die Sperre legt eigene Properties auf der **Instanz** an, die die `Element.prototype`-Member
 * überschatten – kein globaler Prototype-Eingriff, andere Elemente bleiben unberührt. Die Getter
 * von `scrollTop`/`scrollLeft` delegieren an den nativen Descriptor, Positionen bleiben also
 * korrekt lesbar (`computeSelectedNotePosition`, der `scroll`-Listener); nur die **Setter** werden
 * verworfen.
 */
export function lockScrollContainer(el: HTMLElement): () => void {
    for (const name of SCROLL_METHODS) {
        const native = el[name].bind(el);
        Object.defineProperty(el, name, {
            configurable: true,
            writable: true,
            value: (...args: unknown[]): void => {
                if (bypass) {
                    (native as (...a: unknown[]) => void)(...args);
                    return;
                }
                warn(`${name}()`);
            },
        });
    }

    for (const name of SCROLL_OFFSETS) {
        const nativeDescriptor = Object.getOwnPropertyDescriptor(Element.prototype, name);
        if (!nativeDescriptor?.get || !nativeDescriptor.set) continue;
        const {get: nativeGet, set: nativeSet} = nativeDescriptor;
        Object.defineProperty(el, name, {
            configurable: true,
            get: () => nativeGet.call(el) as number,
            set: (value: number) => {
                if (bypass) {
                    nativeSet.call(el, value);
                    return;
                }
                warn(`${name} = ${value}`);
            },
        });
    }

    return () => {
        for (const name of [...SCROLL_METHODS, ...SCROLL_OFFSETS]) {
            delete (el as unknown as Record<string, unknown>)[name];
        }
    };
}
